// ONE place that reads real tables + columns from a dataset's source, for the
// Schema and Business Area designers.
//
// Both designers import tables and both need to offer a table's real columns
// when a join key is being picked. That resolution used to be inlined in each
// designer's Import modal, so a table's columns were only ever knowable at
// import time — which is why a model written with a partial column list (an
// agent curating a 254-column master down to 7) left the join dropdowns unable
// to offer the actual join key, with no way to select it.
//
// A dataset's source is either a registered BI connection (Oracle / Dremio /
// warehouse) when CONNECTION_ID is set, or the project's own Database_Design.
// Callers pass the connection id (blank = project DB) and never branch on it.

import { introspectBiConnection } from '@/api/connections'
import { getTables, getTableInfo } from '@/api/designers'
import type { SchemaTableColumn } from './types'

/** Title-case a DB name: ITEM_CODE → Item Code */
export function prettify(name: string): string {
  return (name || '')
    .toLowerCase().replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase()).trim()
}

/** Map a SQL data type to the schema COLTYPE enum. */
export function mapSqlType(dataType: string): 'CHAR' | 'NUMBER' | 'DATE' {
  const t = (dataType || '').toLowerCase()
  if (/(date|time|timestamp)/.test(t)) return 'DATE'
  if (/(int|num|dec|float|double|money|real|serial|bigint|smallint)/.test(t)) return 'NUMBER'
  return 'CHAR'
}

/** Optional physical detail (native type, nullability, key, default) carried
 *  through from an introspection row when the source reports it. */
export function extendedColProps(c: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  const nativeRaw = c.native_type ?? c.data_type ?? c.type
  if (nativeRaw) out.NATIVETYPE = String(nativeRaw)
  if (c.nullable !== undefined) out.NULLABLE = !!c.nullable
  if (c.mandatory !== undefined) out.mandatory = !!c.mandatory
  if (c.required !== undefined) out.mandatory = !!c.required
  if (c.key !== undefined) out.KEY = !!c.key
  const def = c.default_value ?? c.default ?? c.DEFAULT ?? c.column_default
  if (def !== undefined && def !== null && def !== '') out.DEFAULT_VALUE = String(def)
  return out
}

function blankColumn(): SchemaTableColumn {
  return {
    DBNAME: '', DISPLAY_NAME: '', COLTYPE: 'CHAR', DBSIZE: 100,
    NATIVETYPE: 'AN', JAVATYPE: 'java.lang.String', DEFAULTFUNCTION: '',
    ROW_SECURITY: false, COL_SECURITY: false, HIDDEN: false,
  }
}

/** The dataset-unique name of a column: `TABLE_COLUMN`. Used as the SELECT
 *  alias by every schema-mode visual, so two tables sharing a column name
 *  (ITEM.DESCR + SITE.DESCR) never collide in the query or the result keys. */
export function uniqueColumnName(table: string, dbname: string): string {
  const t = (table || '').trim().toUpperCase()
  const c = (dbname || '').trim().toUpperCase()
  if (!c) return ''
  return t ? `${t}_${c}` : c
}

function toColumn(c: Record<string, unknown>, table: string): SchemaTableColumn {
  const name = String(c.column || c.column_name || c.name || c.DBNAME || '').toUpperCase()
  return {
    ...blankColumn(),
    ...extendedColProps(c),
    DBNAME: name,
    UNIQUE_NAME: uniqueColumnName(table, name),
    DISPLAY_NAME: prettify(name),
    COLTYPE: mapSqlType(String(c.data_type || c.type || '')),
    DBSIZE: (c.size as string | number) || 100,
  }
}

function names(arr: unknown): string[] {
  return (Array.isArray(arr) ? arr : [])
    .map((t) => (typeof t === 'string'
      ? t
      : String((t as { table?: string; name?: string })?.table
        || (t as { name?: string })?.name || '')))
    .filter(Boolean)
}

/** Every table (and view) the dataset's source offers. */
export async function listSourceTables(
  project: string, connectionId?: string,
): Promise<string[]> {
  const conn = (connectionId || '').trim()
  if (conn) {
    const r = await introspectBiConnection(project, conn)
    return [...names(r.tables), ...names(r.views)]
  }
  const r = await getTables(project)
  return names((r as { tables?: unknown[] }).tables)
}

/** The COMPLETE physical column list of one table, from the live source.
 *
 *  Never filtered or sampled: callers decide which columns to keep (the
 *  designer's Import) or which to offer (the join key picker). Throws on a
 *  failed lookup so the caller can surface it rather than show an empty list
 *  that reads as "this table has no columns". */
export async function fetchTableColumns(
  project: string, connectionId: string | undefined, table: string,
): Promise<SchemaTableColumn[]> {
  const conn = (connectionId || '').trim()
  const name = (table || '').trim()
  if (!name) return []
  if (conn) {
    const info = await introspectBiConnection(project, conn, { tables: name })
    const list = Array.isArray(info.tables) ? info.tables : []
    const entry = list.find((x) => String((x as { table?: string })?.table || '')
      .toLowerCase() === name.toLowerCase()) || list[0]
    const raw = Array.isArray((entry as { columns?: unknown[] })?.columns)
      ? ((entry as { columns: Record<string, unknown>[] }).columns) : []
    return raw.map((c) => toColumn(c, name))
  }
  const info = await getTableInfo(project, name)
  const tbl = (info as { table?: Record<string, unknown> })?.table || {}
  const raw = Array.isArray(tbl.columns) ? (tbl.columns as Record<string, unknown>[]) : []
  return raw.map((c) => toColumn(c, name))
}
